
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Users, ExternalLink } from 'lucide-react';
import { Team } from '@/types/database';

interface TeamCardProps {
  team: Team;
  memberCount?: number;
  sport?: string;
  location?: string;
  onViewDetails?: () => void;
}

export const TeamCard = ({ 
  team, 
  memberCount = 0, 
  sport = 'Football', 
  location = 'Location TBD',
  onViewDetails 
}: TeamCardProps) => {
  return (
    <Card className="bg-gray-900 border-gray-700 overflow-hidden hover:bg-gray-800 transition-all duration-300 hover:shadow-lg hover:shadow-sports-green/20">
      <div className="relative">
        {/* Team background image with overlay */}
        <div 
          className="h-56 relative"
          style={{
            backgroundImage: team.team_logo 
              ? `linear-gradient(135deg, rgba(16, 185, 129, 0.8) 0%, rgba(59, 130, 246, 0.8) 50%, rgba(139, 69, 19, 0.8) 100%), url(${team.team_logo})`
              : 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(59, 130, 246, 0.9) 50%, rgba(139, 69, 19, 0.9) 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundBlendMode: 'overlay'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/60" />
          
          {/* Sport badge */}
          <Badge className="absolute top-4 left-4 bg-sports-green text-black font-bold px-3 py-1 text-sm">
            {sport}
          </Badge>
          
          {/* Member count */}
          <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/70 backdrop-blur-sm px-3 py-2 rounded-full border border-white/20">
            <Users className="h-4 w-4 text-white" />
            <span className="text-white font-semibold">{memberCount} Total</span>
          </div>
          
          {/* Team name */}
          <div className="absolute bottom-16 left-4 right-20">
            <h3 className="text-3xl font-bold text-white drop-shadow-lg">
              {team.team_name}
            </h3>
          </div>
          
          {/* Team circle avatar/logo */}
          <div className="absolute bottom-4 right-4">
            <div className="w-20 h-20 rounded-full border-4 border-white bg-white overflow-hidden shadow-lg">
              {team.team_logo ? (
                <img 
                  src={team.team_logo} 
                  alt={`${team.team_name} logo`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-sports-green to-blue-600 flex items-center justify-center">
                  <span className="text-white font-bold text-xl">
                    {team.team_name.charAt(0)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <CardContent className="p-6 bg-gray-900">
        {/* Location */}
        <div className="flex items-center gap-2 text-gray-300 mb-4">
          <MapPin className="h-5 w-5 text-sports-green" />
          <span className="font-medium">{location}</span>
        </div>
        
        {/* View Details Button */}
        <Button 
          className="w-full bg-sports-green text-black hover:bg-green-400 font-bold py-3 text-lg transition-all duration-200"
          onClick={onViewDetails}
        >
          View Details
          <ExternalLink className="h-5 w-5 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};
